/* ✦ Product tabs — lazy tab panels, sliding ink, add-to-bag + variant/quantity popover */
(function () {
  if (customElements.get('bz-product-tabs')) return;

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = (cents) => (window.BZ && window.BZ.money ? window.BZ.money(cents) : (cents / 100).toFixed(2));

  async function addToCart(items, onDone) {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    const target = cartDrawer || cartNotification;
    const body = { items };
    if (target && typeof target.getSectionsToRender === 'function') {
      body.sections = target.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag');
    if (onDone) onDone();
    if (target && typeof target.renderContents === 'function' && data.sections) {
      if (cartDrawer) cartDrawer.classList.remove('is-empty');
      // Dawn's cart drawer expects the first added item as the payload root.
      const payload = Object.assign({}, data.items ? data.items[0] : data, { sections: data.sections });
      target.renderContents(payload);
    } else if (window.BZ) {
      window.BZ.toast(onDone && onDone.message ? onDone.message : 'Added to your bag', (window.routes && window.routes.cart_url) || '/cart', 'View bag');
    }
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bz-product-tabs', cartData: data });
    }
    return data;
  }

  class BzProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.ink = this.querySelector('.bz-pt__ink');
      this.list = this.querySelector('.bz-pt__list');
      this.all = this.querySelector('[data-pt-all]');
      this.allLabel = this.querySelector('[data-pt-all-label]');
      this.pop = this.querySelector('[data-pt-pop]');
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.cache = new Map();
      this.showQv = this.dataset.showQv !== 'false';

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('mouseenter', () => this.prefetch(i), { passive: true });
        tab.addEventListener('focus', () => this.prefetch(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
          e.preventDefault();
          const n = this.tabs.length;
          let next = i + (e.key === 'ArrowRight' ? 1 : -1);
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = n - 1;
          next = (next + n) % n;
          this.select(next);
          this.tabs[next].focus();
        });
      });

      this.moveInk(false);
      this.onResize = () => this.moveInk(false);
      window.addEventListener('resize', this.onResize, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.moveInk(false));

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-hc-atc]');
        if (btn) {
          e.preventDefault();
          this.onAtc(btn);
        }
      });
      this.initPopover();

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.tabs.indexOf(e.target);
          if (i > -1) this.select(i);
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    /* ---------- Tabs ---------- */
    panelFor(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    select(i) {
      this.closePop();
      this.tabs.forEach((tab, k) => {
        const on = k === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = this.panelFor(tab);
        if (!panel) return;
        if (on) {
          panel.hidden = false;
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
          if (panel.hasAttribute('data-lazy')) this.load(tab, panel);
          else if (window.BZ) window.BZ.init(panel);
        } else {
          panel.hidden = true;
        }
      });
      const tab = this.tabs[i];
      if (this.all && tab) {
        this.all.href = tab.dataset.url;
        if (this.allLabel) this.allLabel.textContent = tab.dataset.viewAll;
      }
      this.moveInk(true);
      // Keep the active tab visible in the scrollable mobile tab row.
      if (tab && this.list && this.list.scrollWidth > this.list.clientWidth) {
        this.list.scrollTo({ left: tab.offsetLeft - (this.list.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
      }
    }

    moveInk(animate) {
      const active = this.querySelector('[role="tab"][aria-selected="true"]');
      if (!active || !this.ink) return;
      if (!animate) this.ink.style.transition = 'none';
      this.ink.style.width = `${active.offsetWidth}px`;
      this.ink.style.transform = `translateX(${active.offsetLeft}px)`;
      if (!animate) {
        void this.ink.offsetWidth;
        this.ink.style.transition = '';
      }
    }

    sectionUrl(tab) {
      const u = new URL(tab.dataset.url, window.location.origin);
      u.searchParams.set('section_id', 'bz-tab-products');
      return u.pathname + u.search;
    }

    prefetch(i) {
      const tab = this.tabs[i];
      const panel = tab && this.panelFor(tab);
      if (!panel || !panel.hasAttribute('data-lazy')) return;
      this.fetchItems(tab).catch(() => {});
    }

    fetchItems(tab) {
      const url = this.sectionUrl(tab);
      if (!this.cache.has(url)) {
        this.cache.set(
          url,
          fetch(url, { credentials: 'same-origin' })
            .then((r) => {
              if (!r.ok) throw new Error(r.status);
              return r.text();
            })
            .catch((err) => {
              this.cache.delete(url);
              throw err;
            })
        );
      }
      return this.cache.get(url);
    }

    async load(tab, panel) {
      panel.removeAttribute('data-lazy');
      panel.setAttribute('aria-busy', 'true');
      const grid = panel.querySelector('.bz-pt__grid');
      try {
        const html = await this.fetchItems(tab);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = Array.from(doc.querySelectorAll('[data-pt-items] > li')).slice(0, this.limit);
        grid.innerHTML = '';
        if (!items.length) {
          const li = document.createElement('li');
          li.className = 'bz-pt__empty';
          li.textContent = this.dataset.empty || '';
          grid.appendChild(li);
        }
        items.forEach((li) => {
          this.applyHostOptions(li);
          grid.appendChild(document.importNode(li, true));
        });
        if (window.BZ) window.BZ.init(panel);
      } catch (e) {
        panel.setAttribute('data-lazy', '');
        grid.innerHTML = '';
        if (this.all) {
          const li = document.createElement('li');
          li.className = 'bz-pt__empty';
          const a = document.createElement('a');
          a.className = 'bz-link';
          a.href = tab.dataset.url;
          a.textContent = tab.dataset.viewAll;
          li.appendChild(a);
          grid.appendChild(li);
        }
      } finally {
        panel.removeAttribute('aria-busy');
      }
    }

    applyHostOptions(li) {
      const set = (sel, text) => li.querySelectorAll(sel).forEach((el) => text != null && (el.textContent = text));
      set('[data-hc-label="atc"]', this.dataset.atc);
      set('[data-hc-label="qv"]', this.dataset.qv);
      set('[data-hc-label="sold"]', this.dataset.sold);
      li.querySelectorAll('.bz-hc__badge').forEach((b) => {
        if (b.textContent.trim() === 'Sold out' && this.dataset.sold) b.textContent = this.dataset.sold;
      });
      if (!this.showQv) li.querySelectorAll('.bz-hc__qv').forEach((el) => el.remove());
    }

    /* ---------- Add to bag ---------- */
    async onAtc(btn) {
      const card = btn.closest('[data-hc]');
      if (!card) return;
      if (card.dataset.multi === 'true') {
        this.openPop(card, btn);
        return;
      }
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const done = () => {};
        done.message = this.dataset.added;
        await addToCart([{ id: Number(card.dataset.variantId), quantity: 1 }], done);
      } catch (err) {
        if (window.BZ) window.BZ.toast(err.message);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }

    /* ---------- Popover ---------- */
    initPopover() {
      if (!this.pop) return;
      this.popImg = this.pop.querySelector('[data-pt-pop-img]');
      this.popTitle = this.pop.querySelector('[data-pt-pop-title]');
      this.popPrice = this.pop.querySelector('[data-pt-pop-price]');
      this.popOptions = this.pop.querySelector('[data-pt-pop-options]');
      this.popQty = this.pop.querySelector('[data-pt-qty-input]');
      this.popAtc = this.pop.querySelector('[data-pt-pop-atc]');
      this.popError = this.pop.querySelector('[data-pt-pop-error]');
      this.atcLabel = this.popAtc.textContent.trim();

      this.pop.querySelector('[data-pt-pop-close]').addEventListener('click', () => this.closePop(true));
      this.pop.querySelectorAll('[data-pt-qty]').forEach((b) =>
        b.addEventListener('click', () => {
          this.popQty.value = Math.max(1, (parseInt(this.popQty.value, 10) || 1) + Number(b.dataset.ptQty));
        })
      );
      this.popOptions.addEventListener('change', () => this.syncVariant());
      this.popAtc.addEventListener('click', () => this.popAdd());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.pop.hidden) this.closePop(true);
      });
      document.addEventListener('click', (e) => {
        if (this.pop.hidden) return;
        if (this.pop.contains(e.target) || e.target.closest('[data-hc-atc]')) return;
        this.closePop();
      });
      window.addEventListener('resize', () => this.positionPop(), { passive: true });
    }

    async openPop(card, trigger) {
      this.popCard = card;
      this.popTrigger = trigger;
      this.popError.hidden = true;
      this.popQty.value = 1;
      this.popTitle.textContent = card.dataset.title;
      this.popPrice.textContent = money(card.dataset.price);
      if (card.dataset.thumb) this.popImg.src = card.dataset.thumb;
      else this.popImg.removeAttribute('src');
      this.popOptions.innerHTML = '';
      this.popAtc.disabled = true;
      this.pop.hidden = false;
      this.positionPop();
      requestAnimationFrame(() => this.pop.classList.add('is-open'));

      try {
        const product = await this.fetchProduct(card.dataset.url);
        if (this.popCard !== card) return;
        this.popProduct = product;
        this.renderOptions(product, Number(card.dataset.variantId));
        this.syncVariant();
        const first = this.popOptions.querySelector('select') || this.popAtc;
        first.focus({ preventScroll: true });
      } catch (e) {
        this.popError.textContent = e.message;
        this.popError.hidden = false;
      }
    }

    fetchProduct(url) {
      this.productCache = this.productCache || new Map();
      const key = url.split('?')[0];
      if (!this.productCache.has(key)) {
        this.productCache.set(
          key,
          fetch(`${key}.js`, { headers: { Accept: 'application/json' } }).then((r) => {
            if (!r.ok) throw new Error('Could not load options');
            return r.json();
          })
        );
      }
      return this.productCache.get(key);
    }

    renderOptions(product, selectedId) {
      const selected = product.variants.find((v) => v.id === selectedId) || product.variants.find((v) => v.available) || product.variants[0];
      const html = product.options
        .map((opt, i) => {
          const name = typeof opt === 'string' ? opt : opt.name;
          const values = typeof opt === 'string' ? [...new Set(product.variants.map((v) => v.options[i]))] : opt.values;
          const id = `PtOpt-${product.id}-${i}`;
          return `<div><label class="bz-pt__pop-label" for="${id}">${esc(name)}</label><select id="${id}" data-index="${i}">${values
            .map((val) => `<option value="${esc(val)}"${selected.options[i] === val ? ' selected' : ''}>${esc(val)}</option>`)
            .join('')}</select></div>`;
        })
        .join('');
      this.popOptions.innerHTML = html;
    }

    syncVariant() {
      const product = this.popProduct;
      if (!product) return;
      const chosen = Array.from(this.popOptions.querySelectorAll('select')).map((s) => s.value);
      const variant = product.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      this.popVariant = variant;
      if (!variant) {
        this.popAtc.disabled = true;
        this.popAtc.textContent = 'Unavailable';
        return;
      }
      this.popPrice.textContent = money(variant.price);
      if (variant.featured_image && variant.featured_image.src) {
        this.popImg.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2');
      }
      this.popAtc.disabled = !variant.available;
      this.popAtc.textContent = variant.available ? this.atcLabel : this.dataset.sold || 'Sold out';
    }

    positionPop() {
      if (!this.pop || this.pop.hidden || !this.popCard) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      if (mobile) {
        this.pop.dataset.side = 'bottom';
        this.pop.style.left = '';
        this.pop.style.top = '';
        return;
      }
      const host = this.getBoundingClientRect();
      const card = this.popCard.getBoundingClientRect();
      const popW = this.pop.offsetWidth;
      const gap = 12;
      let side = 'right';
      let left = card.right - host.left + gap - card.width * 0.08;
      if (card.right + popW + gap > window.innerWidth - 8) {
        side = 'left';
        left = card.left - host.left - popW - gap + card.width * 0.08;
      }
      if (left < 0) left = Math.max(0, card.left - host.left);
      const top = card.top - host.top + card.height * 0.1;
      this.pop.dataset.side = side;
      this.pop.style.left = `${left}px`;
      this.pop.style.top = `${top}px`;
      this.pop.style.setProperty('--bz-pop-caret', `${Math.min(card.height * 0.2, 14 * 10)}px`);
    }

    closePop(restoreFocus) {
      if (!this.pop || this.pop.hidden) return;
      this.pop.classList.remove('is-open');
      const trigger = this.popTrigger;
      setTimeout(() => {
        if (!this.pop.classList.contains('is-open')) this.pop.hidden = true;
      }, 250);
      this.popCard = null;
      this.popProduct = null;
      if (restoreFocus && trigger) trigger.focus();
    }

    async popAdd() {
      const variant = this.popVariant || (this.popCard && { id: Number(this.popCard.dataset.variantId) });
      if (!variant) return;
      const qty = Math.max(1, parseInt(this.popQty.value, 10) || 1);
      this.popAtc.classList.add('is-loading');
      this.popError.hidden = true;
      try {
        const done = () => this.closePop();
        done.message = this.dataset.added;
        await addToCart([{ id: variant.id, quantity: qty }], done);
      } catch (err) {
        this.popError.textContent = err.message;
        this.popError.hidden = false;
      } finally {
        this.popAtc.classList.remove('is-loading');
      }
    }
  }

  customElements.define('bz-product-tabs', BzProductTabs);
})();
